import { useState, useCallback, useMemo } from 'react'
import { notifications } from '@/services/index.ts'
import { useWallet } from './useWallet.ts'
import type {
  EmailPayload,
  SMSPayload,
  WhatsAppPayload,
  TelegramPayload,
  PushPayload,
  NotificationResult,
  NotificationRecord,
  NotificationHistoryFilter,
} from '@/services/notification/notification.types.ts'

interface UseNotificationsState {
  loading: boolean
  error: string | null
  pushPermission: NotificationPermission | null
  history: NotificationRecord[]
}

interface UseNotificationsReturn extends UseNotificationsState {
  sendEmail: (payload: EmailPayload) => Promise<NotificationResult | null>
  sendSMS: (payload: SMSPayload) => Promise<NotificationResult | null>
  sendWhatsApp: (payload: WhatsAppPayload) => Promise<NotificationResult | null>
  sendTelegram: (payload: TelegramPayload) => Promise<NotificationResult | null>
  sendPush: (payload: PushPayload) => Promise<NotificationResult | null>
  requestPushPermission: () => Promise<NotificationPermission | null>
  isPushSupported: boolean
  isPushPermitted: boolean
  fetchHistory: (filters?: NotificationHistoryFilter) => Promise<void>
}

export function useNotifications(): UseNotificationsReturn {
  const { address } = useWallet()
  const service = useMemo(() => notifications, [])

  const [state, setState] = useState<UseNotificationsState>({
    loading: false,
    error: null,
    pushPermission:
      typeof window !== 'undefined' && 'Notification' in window
        ? Notification.permission
        : null,
    history: [],
  })

  const setLoading = () =>
    setState((s) => ({ ...s, loading: true, error: null }))
  const setError = (error: string) =>
    setState((s) => ({ ...s, loading: false, error }))
  const setDone = () => setState((s) => ({ ...s, loading: false }))

  const sendEmail = useCallback(
    async (payload: EmailPayload): Promise<NotificationResult | null> => {
      setLoading()
      try {
        const result = await service.sendEmail(payload)
        setDone()
        if (!result.success) {
          setState((s) => ({ ...s, error: result.error ?? 'Email failed' }))
        }
        return result
      } catch (e) {
        setError(e instanceof Error ? e.message : 'Email send failed')
        return null
      }
    },
    [service],
  )

  const sendSMS = useCallback(
    async (payload: SMSPayload): Promise<NotificationResult | null> => {
      setLoading()
      try {
        const result = await service.sendSMS(payload)
        setDone()
        if (!result.success) {
          setState((s) => ({ ...s, error: result.error ?? 'SMS failed' }))
        }
        return result
      } catch (e) {
        setError(e instanceof Error ? e.message : 'SMS send failed')
        return null
      }
    },
    [service],
  )

  const sendWhatsApp = useCallback(
    async (payload: WhatsAppPayload): Promise<NotificationResult | null> => {
      setLoading()
      try {
        const result = await service.sendWhatsApp(payload)
        setDone()
        if (!result.success) {
          setState((s) => ({
            ...s,
            error: result.error ?? 'WhatsApp failed',
          }))
        }
        return result
      } catch (e) {
        setError(e instanceof Error ? e.message : 'WhatsApp send failed')
        return null
      }
    },
    [service],
  )

  const sendTelegram = useCallback(
    async (payload: TelegramPayload): Promise<NotificationResult | null> => {
      setLoading()
      try {
        const result = await service.sendTelegram(payload)
        setDone()
        if (!result.success) {
          setState((s) => ({
            ...s,
            error: result.error ?? 'Telegram failed',
          }))
        }
        return result
      } catch (e) {
        setError(e instanceof Error ? e.message : 'Telegram send failed')
        return null
      }
    },
    [service],
  )

  const sendPush = useCallback(
    async (payload: PushPayload): Promise<NotificationResult | null> => {
      setLoading()
      try {
        const result = await service.sendPush(payload)
        setDone()
        if (!result.success) {
          setState((s) => ({ ...s, error: result.error ?? 'Push failed' }))
        }
        return result
      } catch (e) {
        setError(e instanceof Error ? e.message : 'Push notification failed')
        return null
      }
    },
    [service],
  )

  const requestPushPermission =
    useCallback(async (): Promise<NotificationPermission | null> => {
      try {
        const permission = await service.requestPushPermission()
        setState((s) => ({ ...s, pushPermission: permission }))
        return permission
      } catch (e) {
        setError(e instanceof Error ? e.message : 'Permission request failed')
        return null
      }
    }, [service])

  const fetchHistory = useCallback(
    async (filters?: NotificationHistoryFilter): Promise<void> => {
      if (!address) return
      setLoading()
      try {
        const history = await service.getHistory(address, filters)
        setState((s) => ({ ...s, history, loading: false }))
      } catch (e) {
        setError(e instanceof Error ? e.message : 'Failed to fetch history')
      }
    },
    [address, service],
  )

  return {
    ...state,
    sendEmail,
    sendSMS,
    sendWhatsApp,
    sendTelegram,
    sendPush,
    requestPushPermission,
    isPushSupported: service.isPushSupported(),
    isPushPermitted: service.isPushPermitted(),
    fetchHistory,
  }
}
